import { 
  collection, 
  query, 
  where, 
  orderBy, 
  addDoc, 
  doc, 
  getDoc, 
  Timestamp, 
  limit,
  startAfter,
  DocumentSnapshot,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '../../../auth/firebaseConfig';
import { Conversation } from '../types';

const CONVERSATIONS_COLLECTION = 'conversations';
export const CONVERSATIONS_PAGE_SIZE = 15;

export const conversationService = {
  /**
   * Get all conversations for a specific user
   */
  getUserConversationsQuery: (userId: string) => {
    return query(
      collection(db, CONVERSATIONS_COLLECTION),
      where('participants', 'array-contains', userId),
      orderBy('lastMessageTimestamp', 'desc')
    );
  },

  /**
   * Get paginated conversations for a specific user
   */
  getUserConversationsPaginatedQuery: (
    userId: string, 
    pageSize: number = CONVERSATIONS_PAGE_SIZE, 
    lastDoc?: DocumentSnapshot
  ) => {
    const constraints: QueryConstraint[] = [
      where('participants', 'array-contains', userId),
      orderBy('lastMessageTimestamp', 'desc'),
      limit(pageSize)
    ];

    if (lastDoc) {
      constraints.push(startAfter(lastDoc));
    }

    return query(collection(db, CONVERSATIONS_COLLECTION), ...constraints);
  },

  /**
   * Create a new conversation
   */
  createConversation: async (participantIds: string[], initialMessage?: string): Promise<string> => {
    const now = Timestamp.now();
    const conversationData = {
      participants: participantIds,
      updatedAt: now,
      createdAt: now,
      lastMessage: initialMessage ? {
        content: initialMessage,
        senderId: participantIds[0], // Assumption: first ID is creator
        timestamp: now,
        type: 'text',
        readBy: [participantIds[0]]
      } : null,
      metadata: {
        type: 'direct'
      }
    };

    const docRef = await addDoc(collection(db, CONVERSATIONS_COLLECTION), conversationData);
    return docRef.id;
  },

  /**
   * Get a single conversation by ID
   */
  getConversationById: async (conversationId: string): Promise<Conversation | null> => {
    const docRef = doc(db, CONVERSATIONS_COLLECTION, conversationId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        participantIds: data.participants,
        updatedAt: data.updatedAt,
        createdAt: data.createdAt,
        lastMessage: data.lastMessage,
        ...data
      } as Conversation;
    }
    return null;
  }
};
